import { useWindowSize } from 'usehooks-ts';

export type Breakpoint = 'small' | 'medium' | 'large';

// NOTE: These values must match the breakpoints defined in
// `app/assets/stylesheets/design_system/_breakpoint.scss`.
const SMALL_BREAKPOINT = 568;
const MEDIUM_BREAKPOINT = 1280;

/**
 * Returns the current breakpoint based on the screen size. These breakpoints match the grid
 * layout breakpoints set in `app/assets/stylesheets/design_system/_breakpoint.scss`.
 *
 * 'small': window width < 568px
 * 'medium': window width >= 568px and < 1280px
 * 'large': window width >= 1280px
 */
export const useBreakpoint = (): Breakpoint => {
  const { width } = useWindowSize();

  if (width < SMALL_BREAKPOINT) {
    return 'small';
  } else if (width < MEDIUM_BREAKPOINT) {
    return 'medium';
  } else {
    return 'large';
  }
};

/**
 * Returns whether the screen size is currently within the specified breakpoints.
 *
 * 'small': screen width < 568px
 * 'medium': screen width >= 568px and < 1280px
 * 'large': screen width >= 1280px
 *
 * Basic usage:
 *
 * @example
 * ```tsx
 * import { useInBreakpoints } from 'js/design_system/hooks/useBreakpoint';
 *
 * const isMobile = useInBreakpoints(['small', 'medium']);
 * ```
 */
export const useInBreakpoints = (breakpoints: Breakpoint[]): boolean => {
  const currentBreakpoint = useBreakpoint();

  return breakpoints.includes(currentBreakpoint);
};
